import * as UI from "@goodgym/components";
import * as T from "@goodgym/graphql/types";
import React from "react";
import MissionBadge from "@goodgym/pages/AboutPage/AboutMissionPage/images/badgeMission.png";
import SocialBadge from "@goodgym/test/fixtures/images/streaks/coach-badge.png";

export type RunnerDetailsProps = UI.BoxProps & {
  runner: NonNullable<T.DashboardPageQuery["runner"]>;
};

type TrainingCheckResult = {
  missionTrainingComplete: boolean;
  socialTrainingComplete: boolean;
  missionVerificationProgress: number;
  socialVerificationProgress: number;
  missionVerificationLabel: string;
  socialVerificationLabel: string;
  missionStepsComplete: number;
  socialStepsComplete: number;
  missionStepsTotal: number;
  socialStepsTotal: number;
};

const checkTrainingStatus = (
  runner: NonNullable<T.DashboardPageQuery["runner"]>
): TrainingCheckResult => {
  const missionSlug = "missions-training";
  const socialSlug = "social-visits-training-2024";

  const missionTrainingStatus = runner.trainingStatuses.find(
    (status) => status.slug === missionSlug
  );
  const socialTrainingStatus = runner.trainingStatuses.find(
    (status) => status.slug === socialSlug
  );

  const missionTrainingComplete =
    missionTrainingStatus?.status === "COMPLETED" ||
    missionTrainingStatus?.status === "PASSED";
  const socialTrainingComplete =
    socialTrainingStatus?.status === "COMPLETED" ||
    socialTrainingStatus?.status === "PASSED";

  // mission verification
  const missionCriteria = [runner.verified, missionTrainingComplete];
  const missionStepsComplete = missionCriteria.filter(Boolean).length;
  const missionStepsTotal = missionCriteria.length;

  const missionVerificationProgress = !runner.needsToGetMissionVerified
    ? 100
    : Math.round((missionStepsComplete / missionStepsTotal) * 100);

  const missionVerificationLabel = !runner.needsToGetMissionVerified
    ? "Mission verified"
    : `${missionStepsComplete} / ${missionStepsTotal}`;

  // social verification
  const socialCriteria = [
    runner.verified,
    socialTrainingComplete,
    runner.withReferences,
  ];

  const socialStepsComplete = socialCriteria.filter(Boolean).length;
  const socialStepsTotal = socialCriteria.length;

  const socialVerificationProgress = !runner.needsToGetCoachVerified
    ? 100
    : Math.round((socialStepsComplete / socialStepsTotal) * 100);

  const socialVerificationLabel = !runner.needsToGetCoachVerified
    ? "Social visit verified"
    : `${socialStepsComplete} / ${socialStepsTotal}`;

  return {
    missionTrainingComplete,
    socialTrainingComplete,
    missionVerificationProgress,
    socialVerificationProgress,
    missionVerificationLabel,
    socialVerificationLabel,
    missionStepsComplete,
    missionStepsTotal,
    socialStepsComplete,
    socialStepsTotal,
  };
};



const RunnerDetails: React.FC<React.PropsWithChildren<RunnerDetailsProps>> = ({
  runner,
  ...props
}) => {
  const {
    missionStepsComplete,
    socialStepsComplete,
    missionStepsTotal,
    socialStepsTotal,
    missionVerificationProgress,
    socialVerificationProgress,
    missionVerificationLabel,
    socialVerificationLabel,
  } = checkTrainingStatus(runner);


  const isMissionVerified = missionVerificationProgress === 100;
  const isSocialVerified = socialVerificationProgress === 100;

   const missionTrainingStatus = runner.trainingStatuses.find(
     (status) => status.slug === "missions-training"
   );

  const socialTrainingStatus = runner.trainingStatuses.find(
    (status) => status.slug === "social-visits-training"
  );

  const badge = (
    <UI.Avatar
      {...(props as any)}
      component={UI.Link}
      to={"u.links.profile(runner)"}
      alt={"mission"}
      src={MissionBadge}
      sx={{ height: 31, width: 31, mr: 0.5 }}
    />
  );
  const badge2 = (
    <UI.Avatar
      {...(props as any)}
      component={UI.Link}
      to={"u.links.profile(runner)"}
      alt={"social visit"}
      src={SocialBadge}
      sx={{ height: 31, width: 31, mr: 0.5 }}
    />
  );

  const showMissionSection =
    missionVerificationProgress > 0 ||
    missionTrainingStatus?.status === "INCOMPLETE";

  const showSocialSection =
    socialVerificationProgress > 0 ||
    socialTrainingStatus?.status === "INCOMPLETE";
  const missionTooltip = isMissionVerified
    ? "You are mission verified. You can now sign up to complete missions"
    : `Mission verification allows you to participate in 1-to-1 missions with older people. You've completed ${missionStepsComplete} out of ${missionStepsTotal} steps.`;
  const socialTooltip = isSocialVerified
    ? "You are social visit verified. You can now sign up for social visits"
    : `Social visit verification allows you to be paired with an older person for regular companionship and conversation. You've completed ${socialStepsComplete} out of ${socialStepsTotal} steps.`;
  return (
    <>
      <UI.Grid container direction="column" spacing={1} sx={{ mt: 1 }}>
        {showMissionSection && (
          <UI.Grid
            item
            sx={{
              display: "flex",
              flexDirection: { xs: "row", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <UI.Tooltip
              title={missionTooltip}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor:
                      "linear-gradient(to bottom right, #F5F3FF, #E9E4FF)",
                    color: "black",
                    border: "0.08px solid #FF5F4E",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    animation: "fadeIn 0.3s ease-out, scaleIn 0.2s ease-out",
                    "&:hover": {
                      animation: "pulse 2s infinite",
                    },
                  },
                },
              }}
              enterDelay={500}
              arrow
            >
              <UI.Box sx={{ display: "flex", alignItems: "center" }}>
                <UI.MuiBadge>{badge}</UI.MuiBadge>
                <UI.Chip
                  label={
                    isMissionVerified
                      ? "Mission Verified"
                      : `Mission Not Verified ${missionVerificationLabel}`
                  }
                  color={isMissionVerified ? "success" : "warning"}
                  variant="filled"
                  size="small"
                  sx={{ fontWeight: "bold", fontSize: "12px" }}
                />
              </UI.Box>
            </UI.Tooltip>
            <UI.Box>
              {/* using HTML anchor tag in this to prevent react router issues with url params */}
              <a
                href={
                  isMissionVerified
                    ? "/v3/sessions?type=Mission"
                    : "/my/missions/verification"
                }
                style={{
                  fontSize: "12px",
                  marginTop: 0,
                  textDecoration: "none",
                  color: "#E11018",
                }}
              >
                {isMissionVerified
                  ? "Sign up for a Mission"
                  : "Finish Verification"}
              </a>
            </UI.Box>
          </UI.Grid>
        )}

        {showSocialSection && (
          <UI.Grid
            item
            sx={{
              display: "flex",
              flexDirection: { xs: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <UI.Tooltip
              title={socialTooltip}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor:
                      "linear-gradient(to bottom right, #F5F3FF, #E9E4FF)",
                    color: "black",
                    border: "0.08px solid #FF5F4E",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    animation: "fadeIn 0.3s ease-out, scaleIn 0.2s ease-out",
                    "&:hover": {
                      animation: "pulse 2s infinite",
                    },
                  },
                },
              }}
              enterDelay={500}
              arrow
            >
              <UI.Box sx={{ display: "flex", alignItems: "center" }}>
                <UI.MuiBadge>{badge2}</UI.MuiBadge>
                <UI.Chip
                  label={
                    isSocialVerified
                      ? "Social Visit Verified"
                      : `Social Visit Not Verified ${socialVerificationLabel}`
                  }
                  color={isSocialVerified ? "success" : "warning"}
                  variant="filled"
                  size="small"
                  sx={{ fontWeight: "bold", fontSize: "12px" }}
                />
              </UI.Box>
            </UI.Tooltip>

            <UI.Link
              href={
                isSocialVerified
                  ? "/v3/social-visits"
                  : "/my/coach/verification"
              }
              variant="body2"
              sx={{ fontSize: "12px", mt: { xs: 1, md: 0 } }}
            >
              {isSocialVerified ? "Find a Social Visit" : "Finish Verification"}
            </UI.Link>
          </UI.Grid>
        )}

        {/* {!runner.isDonating && (
          <UI.Grid
            item
            sx={{
              display: "flex",
              flexDirection: { xs: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap:2,
              ml: 4,
             
            }}
          >
            <UI.Chip
              label="Not Donating"
              color="default"
              variant="filled"
              size="small"
              sx={{ fontWeight: "bold", fontSize: "12px" }}
            />

            <UI.Link
              href="/my/settings/donations"
              variant="body2"
              sx={{ fontSize: "12px", mt: { xs: 1, md: 0 } }}
            >
              Donate now
            </UI.Link>
          </UI.Grid>
        )} */}
      </UI.Grid>

      <UI.Divider sx={{ my: 4 }} />
    </>
  );
};

export default RunnerDetails;
